import React, {useEffect, useRef, useState} from 'react';
import {useTranslation} from 'react-i18next';
import Fab from '@mui/material/Fab';
import Box from '@mui/material/Box';
import IconButton from '@mui/material/IconButton';
import Typography from '@mui/material/Typography';
import ChatIcon from '@mui/icons-material/Chat';
import CloseIcon from '@mui/icons-material/Close';

import {usePageContext} from '../../context/PageContext';
import {getUsercentricsServices, waitForApi} from '../../utility/consentManagement/consentUtils';

const CHATBOT_IFRAME_SRC =
    'https://copilotstudio.microsoft.com/environments/20625f60-e097-e945-b2e5-486fed057353/bots/new_SpenderFAQRelease/webchat?__version__=2';

const CHATBOT_IFRAME_HEIGHT = 400;

const USERCENTRICS_CHATBOT_SERVICE_NAME = 'Copilot Webchat';

function CCOChatbot() {
    const {chatbotEnabled} = usePageContext();
    const {t} = useTranslation();
    const [open, setOpen] = useState(false);
    const [serviceConfigured, setServiceConfigured] = useState(false);
    const fabRef = useRef<HTMLButtonElement>(null);
    const closeButtonRef = useRef<HTMLButtonElement>(null);

    useEffect(() => {
        if (open) {
            // Stößt nach dem Mounten des iFrames die Consent-Prüfung an.
            window.dispatchEvent(new Event('resize'));
        }
    }, [open]);

    // Steuert Fokus und Escape-Taste, solange das Panel geöffnet ist.
    useEffect(() => {
        if (!open) {
            return;
        }
        closeButtonRef.current?.focus();

        const handleKeyDown = (event: KeyboardEvent) => {
            if (event.key === 'Escape') {
                setOpen(false);
            }
        };
        window.addEventListener('keydown', handleKeyDown);

        return () => {
            window.removeEventListener('keydown', handleKeyDown);
        };
    }, [open]);

    // Gibt den Fokus beim Schließen an den Toggle-Button zurück (der erst bei
    // geschlossenem Panel gerendert wird).
    const wasOpenRef = useRef(open);
    useEffect(() => {
        if (wasOpenRef.current && !open) {
            fabRef.current?.focus();
        }
        wasOpenRef.current = open;
    }, [open]);

    useEffect(() => {
        if (!chatbotEnabled) {
            return;
        }
        let cancelled = false;

        waitForApi().then(() => {
            if (cancelled) {
                return;
            }
            const services = getUsercentricsServices();
            setServiceConfigured(
                Object.values(services).some(
                    (candidate) => candidate.name === USERCENTRICS_CHATBOT_SERVICE_NAME
                )
            );
        });

        return () => {
            cancelled = true;
        };
    }, [chatbotEnabled]);

    // Blendet das Icon aus, solange der Usercentrics-Service fehlt.
    if (!chatbotEnabled || !serviceConfigured) {
        return null;
    }

    // Ersetzt den durch disableRipple entfernten Fokusindikator.
    const focusStyle = {
        '&:focus-visible::after': {
            content: '""',
            position: 'absolute',
            top: -4,
            left: -4,
            right: -4,
            bottom: -4,
            border: '2px solid var(--focus-state-blue)',
            pointerEvents: 'none',
        },
    };

    const fabStyle = {
        position: 'fixed',
        bottom: 24,
        right: 24,
        zIndex: 1200,
        backgroundColor: 'var(--sos-chatbot-icon)',
        color: '#fff',
        '&:hover': {
            backgroundColor: 'var(--sos-chatbot-icon)',
            filter: 'brightness(0.9)',
        },
        ...focusStyle,
    };

    const panelStyle = {
        position: 'fixed',
        zIndex: 1200,
        display: 'flex',
        flexDirection: 'column',
        backgroundColor: 'background.paper',
        boxShadow: '0 2px 12px 0 rgba(0,0,0,.25)',
        overflow: 'hidden',

        top: {xs: 0, sm: 'auto'},
        left: {xs: 0, sm: 'auto'},
        right: {xs: 0, sm: 24},
        bottom: {xs: 0, sm: 24},

        width: {xs: '100%', sm: 380},
        height: {xs: '100dvh', sm: 'auto'},

        borderRadius: {xs: 0, sm: '8px'},
    };

    const headerStyle = {
        display: 'flex',
        flexDirection: 'row',
        alignItems: 'center',
        justifyContent: 'space-between',
        flexShrink: 0,
        padding: '4px 4px 4px 16px',
        backgroundColor: 'var(--sos-main)',
        color: '#fff',
    };

    return (
        <>
            {open && (
                <Box sx={panelStyle} data-cypress="chatbotPanel">
                    <Box sx={headerStyle}>
                        <Typography variant="subtitle1">
                            {t('chatbot.title')}
                        </Typography>

                        <IconButton
                            ref={closeButtonRef}
                            aria-label={t('chatbot.close_aria')!}
                            onClick={() => setOpen(false)}
                            sx={{color: 'inherit', position: 'relative', ...focusStyle}}
                        >
                            <CloseIcon/>
                        </IconButton>
                    </Box>

                    <Box
                        component="iframe"
                        title={t('chatbot.title')!}
                        data-src={CHATBOT_IFRAME_SRC}
                        data-service={USERCENTRICS_CHATBOT_SERVICE_NAME}
                        data-testid="chatbotIframe"
                        frameBorder={0}
                        sx={{
                            display: 'block',
                            width: '100%',
                            border: 0,
                            height: {xs: 'auto', sm: CHATBOT_IFRAME_HEIGHT},
                            minHeight: {xs: 0, sm: CHATBOT_IFRAME_HEIGHT},
                            flex: {
                                xs: '1 1 0',
                                sm: `0 0 ${CHATBOT_IFRAME_HEIGHT}px`,
                            },
                        }}
                    />
                </Box>
            )}

            {!open && (
                <Fab
                    ref={fabRef}
                    aria-label={t('chatbot.toggle_aria')!}
                    onClick={() => setOpen(true)}
                    sx={fabStyle}
                    data-cypress="chatbotToggle"
                    data-testid="chatbotToggle"
                >
                    <ChatIcon/>
                </Fab>
            )}
        </>
    );
}

export default CCOChatbot;